import React from 'react';
import { Bell, Heart, MessageCircle, UserPlus, Clock, CheckCheck, X } from 'lucide-react';
import { useNotifications, Notification } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onClose }) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, loading } = useNotifications();
  const { t } = useLanguage();

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'like':
        return <Heart className="text-red-400" size={18} />;
      case 'comment':
        return <MessageCircle className="text-blue-400" size={18} />;
      case 'follow':
        return <UserPlus className="text-green-400" size={18} />;
      case 'aging_complete':
        return <Clock className="text-gold-500" size={18} />;
      case 'reminder':
        return <Bell className="text-brand-300" size={18} />;
      default:
        return <Bell className="text-brand-300" size={18} />;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 md:hidden"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed md:absolute right-0 top-0 md:top-full mt-0 md:mt-2 w-full md:w-96 max-h-[80vh] bg-brand-900 border border-brand-800 rounded-none md:rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-brand-800 bg-brand-950">
          <div className="flex items-center gap-2">
            <Bell className="text-gold-500" size={20} />
            <h3 className="font-bold text-brand-50">{t('notifications.title')}</h3>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-brand-400 hover:text-gold-500 transition-colors flex items-center gap-1"
                title={t('notifications.markAllRead')}
              >
                <CheckCheck size={16} />
                <span className="hidden sm:inline">{t('notifications.markAllRead')}</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 text-brand-400 hover:text-brand-200 transition-colors md:hidden"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Notification List */}
        <div className="overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-brand-400 text-sm">{t('common.loading')}</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="mx-auto mb-3 text-brand-600" size={32} />
              <p className="text-brand-400">{t('notifications.empty')}</p>
            </div>
          ) : (
            <div className="divide-y divide-brand-800">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                  className={`p-4 hover:bg-brand-800/50 transition-colors cursor-pointer ${
                    !notification.read ? 'bg-gold-500/5' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    {/* User Avatar or Icon */}
                    <div className="flex-shrink-0">
                      {notification.data?.fromUserPhoto ? (
                        <img
                          src={notification.data.fromUserPhoto}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-brand-800 flex items-center justify-center">
                          {getIcon(notification.type)}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-brand-100">
                        {notification.data?.fromUserName && (
                          <span className="font-bold">{notification.data.fromUserName} </span>
                        )}
                        <span className="text-brand-300">{notification.message}</span>
                      </p>

                      {notification.data?.cigarBrand && (
                        <p className="text-xs text-gold-500 mt-1">
                          {notification.data.cigarBrand} {notification.data.cigarName}
                        </p>
                      )}

                      <p className="text-xs text-brand-500 mt-1">
                        {formatTime(notification.createdAt)}
                      </p>
                    </div>

                    {/* Unread Indicator */}
                    {!notification.read && (
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-gold-500 rounded-full" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Notification Bell Button component for the nav
export const NotificationBell: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => {
  const { unreadCount } = useNotifications();

  return (
    <button
      onClick={onClick}
      className="relative p-2 text-brand-400 hover:text-gold-500 transition-colors"
    >
      <Bell size={22} />
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
};
